import { generatePath, matchPath } from 'react-router-dom';
import { isChainId } from '@/config/chains';
import { CHAIN_SELECTOR_ROUTE_SITEMAP, ROUTES } from '@/config/routes';
import type { ChainId } from '@/stores/slices/chainSlice';
import { onboardingRouteForChain } from './wallets';

interface ChainChangeLocation {
  pathname: string;
  search?: string;
  hash?: string;
}

const splitLocation = (value: string): ChainChangeLocation => {
  const hashIndex = value.indexOf('#');
  const withoutHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : value.slice(hashIndex);
  const searchIndex = withoutHash.indexOf('?');

  if (searchIndex === -1) {
    return { pathname: withoutHash || '/', hash };
  }

  return {
    pathname: withoutHash.slice(0, searchIndex) || '/',
    search: withoutHash.slice(searchIndex),
    hash,
  };
};

const getLocationParts = (location: string | ChainChangeLocation): Required<ChainChangeLocation> => {
  const parts = typeof location === 'string' ? splitLocation(location) : location;

  return {
    pathname: parts.pathname,
    search: parts.search ?? '',
    hash: parts.hash ?? '',
  };
};

const isPathOrChild = (pathname: string, basePath: string): boolean => {
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
};

const withSearchAndHash = (
  pathname: string,
  search: string,
  hash: string
): string => `${pathname}${search}${hash}`;

const matchesRoute = (routePath: string, pathname: string): boolean => {
  return Boolean(matchPath({ path: routePath, end: true }, pathname));
};

const buildMatchedPath = (
  routePath: string,
  params: Record<string, string>
): string => generatePath(routePath, params);

const isSelectedChainStateRoute = (pathname: string): boolean => {
  return CHAIN_SELECTOR_ROUTE_SITEMAP.preserveSelectedChainState.some((routePath) => {
    return matchesRoute(routePath, pathname);
  });
};

const getDataExplorerPathForChain = (pathname: string, nextChain: ChainId): string => {
  for (const routePath of CHAIN_SELECTOR_ROUTE_SITEMAP.rewriteChainParam) {
    const match = matchPath({ path: routePath, end: true }, pathname);
    const routeChain = match?.params.chain;

    if (routeChain && isChainId(routeChain)) {
      return buildMatchedPath(routePath, {
        ...match.params,
        chain: nextChain,
      } as Record<string, string>);
    }
  }

  if (isSelectedChainStateRoute(pathname)) {
    return pathname;
  }

  return ROUTES.DATA_EXPLORER.ROOT;
};

export const resolveChainChangePath = (
  location: string | ChainChangeLocation,
  nextChain: ChainId
): string | null => {
  const { pathname, search, hash } = getLocationParts(location);

  if (isPathOrChild(pathname, ROUTES.DATA_EXPLORER.ROOT)) {
    const nextPathname = getDataExplorerPathForChain(pathname, nextChain);
    if (nextPathname === ROUTES.DATA_EXPLORER.ROOT && nextPathname !== pathname) {
      return nextPathname;
    }

    return nextPathname ? withSearchAndHash(nextPathname, search, hash) : null;
  }

  if (isPathOrChild(pathname, '/token')) {
    return ROUTES.DATA_EXPLORER.ROOT;
  }

  if (isPathOrChild(pathname, ROUTES.TRADING.ROOT)) {
    if (isSelectedChainStateRoute(pathname)) {
      return withSearchAndHash(pathname, search, hash);
    }

    return ROUTES.TRADING.ROOT;
  }

  if (isPathOrChild(pathname, ROUTES.ONBOARDING)) {
    return onboardingRouteForChain(nextChain);
  }

  return null;
};
